<script setup lang="ts">
  import { computed, onMounted, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'
  import { onKeyStroke, useStyleTag, useUrlSearchParams } from '@vueuse/core'
  
  import { Routes } from '@/const/Routes'
  import { useAuthStore } from '@/stores/auth'
  import type { Presentation } from '@/types/presentation/Presentation'
  import { useAppRouting } from '@/composables/useAppRouting'
  import usePresentation from '@/composables/usePresentation'

  import FocusHeader from '@/components/layout/FocusHeader.vue'
  import Layout from '@/components/layout/Layout.vue'
  import Slide from '@/components/presentation/Slide.vue'
  import VgsButton from '@/components/common/VgsButton.vue'
  import { KsButton } from '@aschehoug/kloss'

  const props = defineProps<{
    element: Presentation
  }>()

  const { isTeacher } = useAuthStore()
  const { t } = useI18n()
  const { getRouteWithParams } = useAppRouting()
  const params = useUrlSearchParams('history')
  useStyleTag('html { overflow: hidden; }')
  const {
    colorPair,
    currentSlideIndex,
    resourceGuide,
    slides,
    goToNextSlide,
    goToPrevSlide,
    goToSlide,
  } = usePresentation(props.element.contentId)

  const { name } = useRoute()

  onKeyStroke('ArrowRight', goToNextSlide)
  onKeyStroke('ArrowLeft', goToPrevSlide)

  const teacherGuideTargetRoute = computed(() => {
    const currentRoute = Object.values(Routes).find(route => route.name === name) ?? Routes.SubjectContentResourceGuide

    switch (currentRoute) {
    case Routes.SubjectResource:
    default:
      return Routes.ResourceGuide
    case Routes.SubjectContent:
      return Routes.SubjectContentResourceGuide
    case Routes.TeacherResourceBankSubjectResource:
      return Routes.TeacherResourceBankResourceGuide
    }
  })

  onMounted(() => goToSlide(parseInt(params.slide as string) - 1 || 0))
  watch(currentSlideIndex, () => params.slide = (currentSlideIndex.value + 1).toString())
</script>

<template>
  <div :class="['presentation-wrapper', colorPair]">
    <Layout is-footer-hidden>
      <template #header>
        <FocusHeader />
      </template>
      <template #default>
        <template
          v-for="(slideContentId, index) in slides"
          :key="slideContentId"
        >
          <Slide
            v-if="currentSlideIndex === index"
            :content-id="slideContentId"
          />
        </template>
        <div
          v-if="slides && slides.length && slides.length > 1"
          class="page-counter"
        >
          <KsButton
            :variant="currentSlideIndex <= 0 ? 'tertiary' : 'border'"
            icon-left="arrow-left"
            shape="round"
            :disabled="currentSlideIndex <= 0"
            class="previous"
            @click="goToPrevSlide"
          />
          <span class="page-label">
            {{ t('pageXofY', {
              x: currentSlideIndex + 1 ?? 1,
              y: slides.length ?? 1,
            }) }}
          </span>
          <KsButton
            variant="primary"
            icon-left="arrow-right"
            shape="round"
            :disabled="currentSlideIndex >= slides.length - 1"
            class="next"
            @click="goToNextSlide"
          />
          <router-link
            v-if="isTeacher && resourceGuide && currentSlideIndex === 0"
            :to="getRouteWithParams(teacherGuideTargetRoute, resourceGuide)"
            class="to-teacher-button"
          >
            <VgsButton
              v-if="isTeacher && resourceGuide"
              type="label"
              variant="quaternary"
              shape="rounded"
              icon-left="info-circle"
              class="vgs-button"
            >
              {{ t('subject.to-teacher') }}
            </VgsButton>
          </router-link>
        </div>
      </template>
    </Layout>
  </div>
</template>

<style lang="scss" scoped>
  .presentation-wrapper {
    background-color: var(--color-pair-background-color);
    height: 100vh;
    overflow: hidden;

    :deep(main) {
      height: 100%;
    }
  }

  .ks-button.next,
  .ks-button.previous {
    @include vgs-media-hover {
      background-color: var(--color-pair-hover-background-color);
    }
  }

  .vgs-button {
    background-color: var(--color-pair-background-color);
    color: var(--color-pair-color);
  }

  .to-teacher-button {
    margin-left: 16px;
  }

  .page-counter {
    background-color: var(--color-pair-background-color);
    color: var(--color-pair-color);
    display: flex;
    align-items: center;
    gap: 16px;
    position: fixed;
    left: 0;
    bottom: 0;
    padding: 24px;
    z-index: 9;

    .previous {
      border-color: var(--color-pair-color);
      color: var(--color-pair-color);
    }

    .page-label {
      font-weight: 500;
      min-width: 88px;
      text-align: center;
    }

    .next {
      color: var(--color-pair-background-color);
      background-color: var(--color-pair-color);
    }
  }
</style>
